import styles from "./Landing.module.scss";
import {Link} from "react-router-dom";

type LandingInfo = {
    contactEmail: string;
    developerName: string;
    originalGameName: string;
    originalGameUrl?: string;
};

type LandingProps = {
    info: LandingInfo;
};

const Landing = ({info}: LandingProps) => {
    return (
        <main className={styles.landing}>
            <header className={styles.hero}>
                <div className={styles.hero__badgeRow}>
                    <span className={styles.badge}>Browser game</span>
                    <span className={styles.badge}>Online multiplayer</span>
                    <span className={styles.badge}>Non-commercial</span>
                </div>

                <h1 className={styles.hero__title}>
                    Low Five — Online Multiplayer Card Game
                </h1>

                <p className={styles.hero__subtitle}>
                    A fast-paced online card game you can play directly in your browser.
                    Create lobbies, invite friends, and track your stats.
                </p>

                <div className={styles.hero__actions}>
                    <Link to="/login" className={`${styles.btn} ${styles["btn--accent"]}`}>
                        Play now
                    </Link>
                    <a href="#how-it-works" className={styles.btn}>
                        How it works
                    </a>
                </div>

                <p className={styles.hero__note}>
                    This is a fan-made, non-commercial project built for fun and learning.
                </p>
            </header>

            <section id="how-it-works" className={styles.section}>
                <h2>How to play Low Five</h2>
                <p>
                    Low Five is a turn-based multiplayer card game with simple rules and short rounds.
                    The goal is to score as few points as possible by playing and discarding cards wisely.
                </p>
                <p className={styles.muted}>
                    Want the full rules? See <a href={info.originalGameUrl} target="_blank" rel="noreferrer">official site</a>.
                </p>
            </section>

            <section className={styles.section}>
                <h2>Game features</h2>

                <div className={styles.features}>
                    <div className={styles.feature}>
                        <strong>♠ Online multiplayer</strong>
                        <p>Create lobbies and play with friends in real time.</p>
                    </div>

                    <div className={styles.feature}>
                        <strong>⚡ Fast rounds</strong>
                        <p>Short games perfect for quick sessions.</p>
                    </div>

                    <div className={styles.feature}>
                        <strong>📊 Player statistics</strong>
                        <p>Track games played, wins, and average points.</p>
                    </div>

                    <div className={styles.feature}>
                        <strong>🌍 Browser-based</strong>
                        <p>No downloads. Works on desktop and mobile.</p>
                    </div>
                </div>
            </section>

            <section className={styles.section}>
                <h2>Start playing</h2>
                <p>
                    Create an account, join a lobby, and start your first game in seconds.
                </p>

                <div className={styles.hero__actions}>
                    <Link to="/signup" className={`${styles.btn} ${styles["btn--accent"]}`}>
                        Create account
                    </Link>
                    <Link to="/login" className={styles.btn}>
                        Log in
                    </Link>
                </div>
            </section>

            <footer className={styles.footer}>
                <div className={styles.footer__grid}>
                    <div className={styles.footer__col}>
                        <div className={styles.footer__brand}>Low Five</div>
                        <div className={styles.footer__muted}>
                            Online card game · non-commercial fan project
                        </div>
                    </div>

                    <div className={styles.footer__col}>
                        <div className={styles.footer__title}>Contact</div>
                        <a className={styles.footer__link} href={`mailto:${info.contactEmail}`}>
                            {info.contactEmail}
                        </a>
                        <div className={styles.footer__muted}>Developer: {info.developerName}</div>
                    </div>
                </div>

                <div className={styles.footer__bottom}>
                    <span>© {new Date().getFullYear()} {info.developerName}</span>

                    <span className={styles.footer__sep}>·</span>

                    <span className={styles.footer__muted}>
                        Inspired by{" "}
                        {info.originalGameUrl ? (
                            <a className={styles.footer__linkInline} href={info.originalGameUrl} target="_blank"
                               rel="noreferrer">
                                {info.originalGameName}
                            </a>
                        ) : (
                            info.originalGameName
                        )}
                    </span>
                </div>
            </footer>
        </main>
    );
};

export default Landing;
